import React from 'react';

import Select from 'react-select';

import { FLAT_SELECT_CLASS, flatWide } from './reconciliationSelectStyles';

import Tooltip from 'CommonComponents/Tooltip';

import LegacyIcon from 'Assets/icons/clock.svg';
import SuggestedIcon from 'Assets/icons/star-filled.svg';

// Group heading + count badge, lifted from CommonComponents/SelectField so the
// two grouped menus in the app look the same.
const groupStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

const groupBadgeStyles = {
    backgroundColor: '#EBECF0',
    borderRadius: '2em',
    color: '#172B4D',
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 'normal',
    lineHeight: '1',
    minWidth: 1,
    padding: '0.16666666666667em 0.5em',
    textAlign: 'center',
};

// A line whose owner could not be resolved still needs a heading, otherwise it
// would silently disappear from a grouped menu.
const UNGROUPED_KEY = '__ungrouped__';

/**
 * The parent path shown for a line, e.g. "Full Circle / Travel Costs".
 *
 * The PARENT is the segment that truncates: the line name is what identifies the
 * allocation, so it must stay fully readable however narrow the cell gets.
 * Exported so the read-only cell renders the same path as the closed control.
 */
export function BudgetLinePath({ parentLabel, name, markers = null }) {
    return (
        <span className="tw-flex tw-items-center tw-gap-x-1 tw-min-w-0 tw-max-w-full">
            {!!parentLabel && (
                <>
                    <span className="tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-charcoal-50">{parentLabel}</span>
                    <span className="tw-shrink-0 tw-text-charcoal-25">/</span>
                </>
            )}
            <span className="tw-shrink-0">{name}</span>
            {markers}
        </span>
    );
}

/**
 * Marker for a template / pack child that is only still listed because an
 * existing allocation points at it. The tooltip is rendered in the menu only —
 * one popper per closed cell across 40 rows would be a lot of dead weight.
 *
 * Exported so the closed, read-only cell (ActualCostsAllocationSubRow) can render
 * the same marker — otherwise a legacy allocation looks identical to every other
 * row until the reconciler happens to click into it.
 */
export function LegacyMarker({ parentLabel, withTooltip }) {
    const marker = (
        <span className="tw-shrink-0 tw-text-charcoal-50">
            <LegacyIcon className="tw-h-2.5 tw-w-2.5" />
        </span>
    );

    if (!withTooltip) {
        return marker;
    }

    const message = parentLabel
        ? $t('Legacy allocation on {{parent}}. That template or pack can now only be allocated at total level — move this allocation by hand.', {
              parent: parentLabel,
          })
        : $t('Legacy allocation. This line can no longer be allocated — move this allocation by hand.');

    return (
        <Tooltip
            placement="right"
            allowTooltip={true}
            tooltipType="text"
            offset={[0, 12]}
            // Wrapped in an element on purpose: TextTooltip pushes STRING children
            // through dangerouslySetInnerHTML, and the template / pack name in this
            // message is user-entered. As an element it goes down the escaped branch.
            tooltipContent={<span>{message}</span>}
        >
            {marker}
        </Tooltip>
    );
}

/**
 * Typed ids of the budget lines the pre-population hints suggest for this
 * allocation, or an empty list when there is no hint to go on.
 */
export function suggestedBudgetLineIds(suggestions, typeKey, typedAllocatableId) {
    const budgetLines = suggestions?.[typeKey]?.[typedAllocatableId]?.budgetLines;

    if (!budgetLines || typeof budgetLines !== 'object') {
        return [];
    }

    return Object.keys(budgetLines);
}

function toOption(lineItem, isSuggested) {
    return {
        value: lineItem.typed_id,
        // A project / template / pack root is the only allocatable level of its
        // sub-tree, so label it as the total it actually represents. This is also
        // what makes a project readable as an option inside its own group header.
        label: lineItem.is_total ? $t('{{name}} — total', { name: lineItem.name }) : lineItem.name,
        group_key: lineItem.group_key ?? null,
        group_label: lineItem.group_label ?? null,
        is_parent: !!lineItem.is_parent,
        is_total: !!lineItem.is_total,
        is_legacy_child: !!lineItem.is_legacy_child,
        legacy_parent_label: lineItem.legacy_parent_label ?? null,
        is_suggested: isSuggested,
        // Listed so an existing allocation stays readable, never selectable again.
        isDisabled: !!lineItem.is_legacy_child,
    };
}

/**
 * Group the per-row budget lines by their `group_key` — a project for anything
 * under one, otherwise the owning department or cost centre.
 *
 * Groups keep FIRST-SEEN order, which is the filter order the payload already
 * arrives in (narrowed by department and by Workforce / Non-Workforce).
 *
 * `Suggested` is deliberately NOT a group of its own: react-select supports one
 * level of grouping and the project / department headings win, so a suggested
 * line keeps its badge and sorts to the top of the group it belongs to.
 */
export function buildBudgetLineGroups(lineItems, suggestedIds = []) {
    if (!Array.isArray(lineItems) || lineItems.length === 0) {
        return [];
    }

    const suggested = new Set(suggestedIds);
    const groups = [];
    const byKey = new Map();

    lineItems.forEach((lineItem) => {
        const groupKey = lineItem.group_key || UNGROUPED_KEY;
        let group = byKey.get(groupKey);

        if (!group) {
            group = { groupKey, label: lineItem.group_label || $t('Other Budget Lines'), options: [] };
            byKey.set(groupKey, group);
            groups.push(group);
        }

        group.options.push(toOption(lineItem, suggested.has(lineItem.typed_id)));
    });

    groups.forEach((group) => {
        group.options = [...group.options.filter((option) => option.is_suggested), ...group.options.filter((option) => !option.is_suggested)];
    });

    return groups;
}

const formatGroupLabel = (group) => (
    <div style={groupStyles}>
        <span>{group.label}</span>
        <span style={groupBadgeStyles}>{group.options.length}</span>
    </div>
);

const formatOptionLabel = (option, { context }) => {
    const markers = (
        <>
            {!!option.is_suggested && (
                <span className="tw-shrink-0 tw-text-green-75" title={$t('Suggested')}>
                    <SuggestedIcon className="tw-h-2.5 tw-w-2.5" />
                </span>
            )}
            {!!option.is_legacy_child && <LegacyMarker parentLabel={option.legacy_parent_label} withTooltip={context === 'menu'} />}
        </>
    );

    // A project root's group_label is its OWN name — by design, so the project
    // total sits inside its own group heading (see the blueprint addendum). Showing
    // it as the parent segment too would read "Full Circle / Full Circle — total".
    // `is_parent` is true only for that root, so it is the signal to suppress it.
    const parentLabel = option.is_parent ? null : option.group_label;

    return <BudgetLinePath parentLabel={parentLabel} name={option.label} markers={markers} />;
};

/**
 * Matching the GROUP label as well as the line name is what makes "search a
 * parent name and see that whole group" work: react-select keeps a heading as
 * long as one of its options survives the filter, so every child of a matched
 * parent stays visible with no pre-filtering of the option list.
 */
const filterOption = (option, rawInput) => {
    const needle = (rawInput || '').trim().toLowerCase();

    if (!needle) {
        return true;
    }

    const data = option.data ?? {};
    const groupLabel = String(data.group_label ?? '').toLowerCase();
    const label = String(data.label ?? option.label ?? '').toLowerCase();

    return groupLabel.includes(needle) || label.includes(needle);
};

/**
 * The hierarchical budget-line dropdown for one allocation.
 *
 * `groups` comes from buildBudgetLineGroups, built by the caller so it can also
 * resolve the current selection against the same list (and notice when the
 * selection has fallen out of it).
 */
export default function BudgetLineSelect({ groups, value, onChange, placeholder }) {
    return (
        <Select
            options={groups}
            value={value}
            onChange={onChange}
            placeholder={placeholder ?? $t('Select…')}
            className={FLAT_SELECT_CLASS}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            filterOption={filterOption}
            styles={flatWide}
        />
    );
}
